/* eslint-disable class-methods-use-this */
import authRoutes from 'routes/codegen/authRoutes';
import appRoutes from 'routes/codegen/appRoutes';
import { handleFeatureRequired, handlePermissionDenied, handleProtectedError } from 'stores/error_handlers';
import { toast } from 'ui';
import Cookies from 'universal-cookie';
import { ErrorContext, FetchParams, Middleware, RequestContext, ResponseContext } from './codegen';
import { HistoryObject } from './types';

class AddCsrfHeaderMiddleware implements Middleware {
  async pre(context: RequestContext): Promise<void | FetchParams> {
    const cookies = new Cookies();

    context.init.headers = {
      ...context.init.headers,
      'X-CSRFToken': cookies.get('csrftoken'),
    };

    return context;
  }
}

class HandleErrorsMiddleware implements Middleware {
  private history: HistoryObject;
  private language: string;

  constructor(history: HistoryObject, language: string) {
    this.history = history;
    this.language = language;
  }

  async onError(context: ErrorContext): Promise<void | Response> {
    if (!context.response || context.response.status === 0) {
      toast.error(
        gettext(
          "We couldn't connect to Retino servers. We'll keep trying but there may be a problem with your connection.",
        ),
      );
    }
  }

  async post({ response }: ResponseContext) {
    if (response.type === 'error') {
      toast.error(
        gettext(
          "We couldn't connect to Retino servers. We'll keep trying but there may be a problem with your connection.",
        ),
      );
    } else if (response.status === 401) {
      window.location = `${authRoutes.authLogin({ lang: this.language })}?next=${this.history.location.pathname}`;
    } else if (response.status === 403) {
      const cloned = response.clone();
      const data = await cloned.json();
      if (data.error === 'two_factor_required' && window.location.pathname !== appRoutes.authTwoFactorRequired.url) {
        window.location.href = appRoutes.authTwoFactorRequired();
        return new Response(JSON.stringify({}), { status: 403 });
      }
      if (data.code === 'feature_required') {
        handleFeatureRequired(data);
        // Replace response body with empty object so Form.tsx error handling
        // finds no field errors and shows no error toasts. The modal handles the UX.
        return new Response(JSON.stringify({}), { status: 403 });
      }
      if (data.code === 'permission_denied') {
        handlePermissionDenied(data.detail);
        return new Response(JSON.stringify({}), { status: 403 });
      }
    } else if (response.status === 409) {
      const data = await response.json();
      if (data.code && data.code === 'protected_error') {
        handleProtectedError(data);
      }
    } else if (response.status === 500) {
      toast.error(gettext('We were not able to process your request. Our developers have been informed.'));
    } else if (response.status >= 501) {
      toast.error(gettext('We were not able to process your request.'));
    }
  }
}

class TrackRequestIdMiddleware implements Middleware {
  async post({ response }: ResponseContext): Promise<void> {
    const requestId = response.headers.get('X-Request-Id');
    if (requestId && (window.Sentry as any)?.setTag) {
      (window.Sentry as any).setTag('request_id', requestId);
    }
  }

  async onError(context: ErrorContext): Promise<void> {
    const requestId = context.response?.headers.get('X-Request-Id');
    if (requestId && (window.Sentry as any)?.setTag) {
      (window.Sentry as any).setTag('request_id', requestId);
    }
  }
}

export default {
  AddCsrfHeaderMiddleware,
  HandleErrorsMiddleware,
  TrackRequestIdMiddleware,
};
